import {
  ButtonGeneral,
  ButtonSecondary,
  CheckButtonTheme,
  IconCheckButton,
  theme,
  typographyRem,
} from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import React, { useContext } from 'react'

import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { lastNameSubstring } from '../../../../utils/lastNameSubstring'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { AddDriverConsentModal } from './AddDriverConsentModal'
import { AdditionalDriversStyle } from './AdditionalDriversStyle'
import { RemoveDriverModal } from './RemoveDriverModal'
const { h4: H4, h5: H5, paragraphBody: ParagraphBody, labelSmall: LabelSmall } = typographyRem

export const AdditionalDrivers = () => {
  const quote = useQuote()
  const {
    state: { showModal, activeModal, selectedDriverIndex },
    handleShowModal,
    handleSelectedDriver,
  } = useContext(ProviderContext)
  const { validateTransition, removeValidationIndex } = usePreQuoteContext()
  const {
    updatedDriverDetails,
    updatedAdditionalDriver,
    setUpdatedAdditionalDriver,
    handleUpdateDrivers,
    handleUpdateSelectedDriver,
    handleDriverEdit,
    additionalDrivers,
    updatingDrivers,
    setUpdatingDrivers,
  } = useDriverPageContext()

  const handleAdditionalDriverName = (driver: any, i: number, shorthand?: boolean) => {
    const firstName = driver?.first_names || 'Driver'
    const formatedLastName =
      shorthand && driver?.last_names ? `${lastNameSubstring(driver?.last_names)}.` : driver?.last_names
    const lastNamePlaceholder = driver?.first_names ? '' : i + 2
    const lastName = formatedLastName && firstName !== 'Driver' ? formatedLastName : lastNamePlaceholder
    return `${firstName} ${lastName}`
  }

  const handleAddDriver = async () => {
    DataLayer.customEvent({
      eventName: 'add drivers',
      section: 'driver',
      eventType: 'form tracking',
    })
    const valid = validateTransition()
    if (!valid) return
    if (additionalDrivers?.length < 1) {
      handleShowModal('driver-consent')
      return
    }
    setUpdatingDrivers(true)
    if (!selectedDriverIndex) {
      await handleUpdateDrivers(updatedDriverDetails, 'setProposer')
    }

    let updatedDrivers = handleUpdateSelectedDriver()
    updatedDrivers = [...updatedDrivers, { claims: [], convictions: [] }]
    await handleUpdateDrivers({ additionalDriver: updatedDrivers }, 'setAdditionalDrivers')

    setUpdatedAdditionalDriver({ claims: [], convictions: [] })
    handleSelectedDriver(updatedDrivers.length)
    setUpdatingDrivers(false)
    document.getElementById('title')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  const handleRemoveDriver = async () => {
    if (selectedDriverIndex - 1 <= additionalDrivers?.length) {
      setUpdatingDrivers(true)
      removeValidationIndex(selectedDriverIndex)
      const updatedDrivers = additionalDrivers
      updatedDrivers.splice(selectedDriverIndex - 1, 1)
      handleSelectedDriver(selectedDriverIndex - 1)
      setUpdatedAdditionalDriver(additionalDrivers[selectedDriverIndex - 2])
      await handleUpdateDrivers({ additionalDriver: updatedDrivers }, 'setAdditionalDrivers')
      setUpdatingDrivers(false)
    }
  }

  return (
    <AdditionalDriversStyle>
      <GenericSectionStyle>
        {showModal && activeModal === 'driver-consent' && (
          <AddDriverConsentModal
            onClick={async () => {
              setUpdatingDrivers(true)
              await handleUpdateDrivers(updatedDriverDetails, 'setProposer')
              await handleUpdateDrivers({ additionalDriver: [{ claims: [], convictions: [] }] }, 'setAdditionalDrivers')
              handleSelectedDriver(1)
              setUpdatingDrivers(false)
              document.getElementById('title')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }}
          />
        )}
        {showModal && activeModal === 'remove-driver' && (
          <RemoveDriverModal
            onRemove={handleRemoveDriver}
            name={handleAdditionalDriverName(updatedAdditionalDriver, selectedDriverIndex - 1)}
          />
        )}
        <H4>Add additional drivers.</H4>
        <div className="generic-wrapper">
          <div className="driver-wrapper">
            <div className="driver-container">
              <div className="driver-info">
                <LabelSmall>Primary driver</LabelSmall>
                <div className="driver-name">
                  <IconCheckButton
                    status={!selectedDriverIndex ? 'inprogress' : 'complete'}
                    iconFill={!selectedDriverIndex ? '' : theme.primaryColours.white.main}
                    backgroundFill={!selectedDriverIndex ? '' : theme.primaryColours.mint.superDarkMint}
                    buttonTheme={CheckButtonTheme.Dark}
                  />
                  <H5>{`${updatedDriverDetails.firstNames || quote?.details?.proposer?.first_names || 'Full'} ${
                    updatedDriverDetails.lastNames || quote?.details?.proposer?.last_names || 'Name'
                  }`}</H5>
                </div>
              </div>
              {!!selectedDriverIndex && (
                <ButtonSecondary
                  label="Edit"
                  onClick={() => handleDriverEdit(-1)}
                  dataCy="primary-driver-edit-button"
                  customIcon="IconMilesInput"
                  disabled={updatingDrivers}
                  tabFocusTrap={showModal ? -1 : 0}
                />
              )}
            </div>
            {additionalDrivers?.length > 0 &&
              additionalDrivers.map((driver, i) => (
                <div className="driver-container">
                  <div className="driver-info">
                    <LabelSmall>Additional driver</LabelSmall>
                    <div className="driver-name">
                      <IconCheckButton
                        status={selectedDriverIndex === i + 1 ? 'inprogress' : 'complete'}
                        iconFill={selectedDriverIndex === i + 1 ? '' : theme.primaryColours.white.main}
                        backgroundFill={selectedDriverIndex === i + 1 ? '' : theme.primaryColours.mint.superDarkMint}
                        buttonTheme={CheckButtonTheme.Dark}
                      />
                      <H5>
                        {selectedDriverIndex === i + 1
                          ? handleAdditionalDriverName(updatedAdditionalDriver, selectedDriverIndex - 1)
                          : handleAdditionalDriverName(driver, i)}
                      </H5>
                    </div>
                  </div>
                  {selectedDriverIndex !== i + 1 && (
                    <ButtonSecondary
                      label="Edit"
                      onClick={() => handleDriverEdit(i)}
                      dataCy={`additional-driver-${i + 1}-edit-button`}
                      customIcon="IconMilesInput"
                      disabled={updatingDrivers}
                      tabFocusTrap={showModal ? -1 : 0}
                    />
                  )}
                  {selectedDriverIndex === i + 1 && (
                    <ButtonSecondary
                      label="Remove"
                      onClick={() => handleShowModal('remove-driver')}
                      dataCy={`additional-driver-${i + 1}-remove-button`}
                      customIcon="IconRemove"
                      disabled={updatingDrivers}
                      tabFocusTrap={showModal ? -1 : 0}
                    />
                  )}
                </div>
              ))}
          </div>
          <div className="additional-button">
            {additionalDrivers?.length < 3 && (
              <ButtonGeneral
                label="Add another driver"
                onClick={handleAddDriver}
                dataCy="add-additional-driver-button"
                icon="IconPlusSmall"
                disabled={updatingDrivers}
                tabFocusTrap={showModal ? -1 : 0}
              />
            )}
            <ParagraphBody className="add-driver-info">
              {additionalDrivers?.length < 3 ? 'You can add ' : "Sorry, you can't have more than "}
              <span className="bold">{additionalDrivers?.length < 3 ? 3 - additionalDrivers?.length : 3}</span>
              {additionalDrivers?.length < 3 ? ' more drivers to your policy.' : ' drivers on your policy.'}
            </ParagraphBody>
          </div>
        </div>
      </GenericSectionStyle>
    </AdditionalDriversStyle>
  )
}
